import React, { useState } from 'react';
import { useAppContext } from '../../../Store/Store';

interface DeviceDataScreenProps {
  onNext: () => void;
  showSkipNextScreenButton?: boolean;
  showBackButton?: boolean;
  onBack?: () => void;
  // Consumer variant also collects purchase date (Screen 2 in the flow).
  showPurchaseDate?: boolean;
  // Consumer variant does not collect the codentify code.
  showCodentify?: boolean;
  // Consumer variant also offers ADC + months as an alternative to cycle
  // count: the backend derives cycle_count as adc * months * 30.
  showAdcMonths?: boolean;
}

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 'clamp(0.8rem, 2.1vh, 0.95rem)',
  marginBottom: 'clamp(0.25rem, 0.8vh, 0.35rem)',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  padding: 'clamp(0.55rem, 1.8vh, 0.75rem)',
  borderRadius: '7px',
  border: '1px solid #07296A',
  background: '#e9eaee',
  fontSize: 'clamp(0.9rem, 2.3vh, 1rem)',
  marginBottom: 'clamp(0.7rem, 2.2vh, 1.25rem)',
};

const disabledInputStyle: React.CSSProperties = {
  ...inputStyle,
  background: '#d8d9dd',
  color: '#8a8d99',
  cursor: 'not-allowed',
};

const DeviceDataScreen: React.FC<DeviceDataScreenProps> = ({
  onNext,
  showSkipNextScreenButton = false,
  showBackButton = false,
  onBack,
  showPurchaseDate = false,
  showCodentify = true,
  showAdcMonths = false,
}) => {
  const { style, deviceData, setDeviceData } = useAppContext();

  const [purchaseDate, setPurchaseDate] = useState(deviceData.purchase_date || '');
  const [cycleCount, setCycleCount] = useState(deviceData.cycle_count || '');
  const [codentify, setCodentify] = useState(deviceData.codentify || '');
  const [adc, setAdc] = useState(deviceData.adc || '');
  const [months, setMonths] = useState(deviceData.months || '');
  const [cycleCountError, setCycleCountError] = useState('');
  const [adcMonthsError, setAdcMonthsError] = useState('');

  // Whichever group the user starts filling in first locks out the other:
  // cycle count and ADC/months are alternatives, never both at once.
  const adcMonthsFilled = adc !== '' || months !== '';
  const cycleCountDisabled = showAdcMonths && adcMonthsFilled;
  const adcMonthsDisabled = showAdcMonths && cycleCount !== '';

  const handleSend = () => {
    const trimmedCycleCount = cycleCount.trim();
    const trimmedAdc = adc.trim();
    const trimmedMonths = months.trim();

    // Only whole integers are allowed; anything else blocks submission.
    if (trimmedCycleCount !== '' && !/^\d+$/.test(trimmedCycleCount)) {
      setCycleCountError('Cycle count must be a whole number');
      return;
    }
    setCycleCountError('');

    if (showAdcMonths) {
      const hasCycleCount = trimmedCycleCount !== '';
      const hasAdc = trimmedAdc !== '';
      const hasMonths = trimmedMonths !== '';

      // ADC + months is an alternative to cycle count, not an addition to it.
      if (hasCycleCount && (hasAdc || hasMonths)) {
        setAdcMonthsError('Enter either cycle count or ADC and months, not both');
        return;
      }
      if (hasAdc !== hasMonths) {
        setAdcMonthsError('ADC and months must both be filled in together');
        return;
      }
      if ((hasAdc && !/^\d+$/.test(trimmedAdc)) || (hasMonths && !/^\d+$/.test(trimmedMonths))) {
        setAdcMonthsError('ADC and months must be whole numbers');
        return;
      }
      setAdcMonthsError('');
    }

    setDeviceData({
      cycle_count: trimmedCycleCount,
      codentify: showCodentify ? codentify.trim() : '',
      purchase_date: purchaseDate,
      adc: showAdcMonths ? trimmedAdc : '',
      months: showAdcMonths ? trimmedMonths : '',
    });
    onNext();
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '90%',
        maxWidth: '1100px',
        margin: '0 auto',
        padding: 'clamp(0.75rem, 2vh, 1.25rem) 0',
        boxSizing: 'border-box',
      }}
    >
      {showPurchaseDate && (
        <div>
          <label htmlFor="dd-purchase-date" style={labelStyle}>
            Purchase date
          </label>
          <input
            id="dd-purchase-date"
            type="date"
            value={purchaseDate}
            onChange={e => setPurchaseDate(e.target.value)}
            style={inputStyle}
          />
        </div>
      )}

      <div>
        <label htmlFor="dd-cycle-count" style={labelStyle}>
          Cycle count
        </label>
        <input
          id="dd-cycle-count"
          type="number"
          inputMode="numeric"
          step="1"
          min="0"
          value={cycleCount}
          disabled={cycleCountDisabled}
          // Keep it a whole number: strip anything that isn't a digit.
          onChange={e => {
            setCycleCount(e.target.value.replace(/\D/g, ''));
            if (cycleCountError) setCycleCountError('');
            if (adcMonthsError) setAdcMonthsError('');
          }}
          style={cycleCountDisabled ? disabledInputStyle : inputStyle}
        />
        {cycleCountError && (
          <p
            style={{
              color: '#b00020',
              fontSize: '85%',
              marginTop: 'calc(-1 * clamp(0.7rem, 2.2vh, 1.25rem) + 0.25rem)',
              marginBottom: 'clamp(0.7rem, 2.2vh, 1.25rem)',
            }}
          >
            {cycleCountError}
          </p>
        )}
      </div>

      {showAdcMonths && (
        <div>
          <label htmlFor="dd-adc" style={labelStyle}>
            Average Daily Cycles
          </label>
          <input
            id="dd-adc"
            type="number"
            inputMode="numeric"
            step="1"
            min="0"
            value={adc}
            disabled={adcMonthsDisabled}
            onChange={e => {
              setAdc(e.target.value.replace(/\D/g, ''));
              if (adcMonthsError) setAdcMonthsError('');
              if (cycleCountError) setCycleCountError('');
            }}
            style={adcMonthsDisabled ? disabledInputStyle : inputStyle}
          />

          <label htmlFor="dd-months" style={labelStyle}>
            Months
          </label>
          <input
            id="dd-months"
            type="number"
            inputMode="numeric"
            step="1"
            min="0"
            value={months}
            disabled={adcMonthsDisabled}
            onChange={e => {
              setMonths(e.target.value.replace(/\D/g, ''));
              if (adcMonthsError) setAdcMonthsError('');
              if (cycleCountError) setCycleCountError('');
            }}
            style={adcMonthsDisabled ? disabledInputStyle : inputStyle}
          />
          {adcMonthsError && (
            <p
              style={{
                color: '#b00020',
                fontSize: '85%',
                marginTop: 'calc(-1 * clamp(0.7rem, 2.2vh, 1.25rem) + 0.25rem)',
                marginBottom: 'clamp(0.7rem, 2.2vh, 1.25rem)',
              }}
            >
              {adcMonthsError}
            </p>
          )}
        </div>
      )}

      {showCodentify && (
        <div>
          <label htmlFor="dd-codentify" style={labelStyle}>
            Codentify
          </label>
          <input
            id="dd-codentify"
            type="text"
            value={codentify}
            onChange={e => setCodentify(e.target.value)}
            style={inputStyle}
          />
        </div>
      )}

      <div style={{ flex: 1 }} />

      <button
        type="button"
        onClick={handleSend}
        style={{
          height: 'clamp(2.75rem, 7vh, 3.4rem)',
          width: '100%',
          borderRadius: '7px',
          border: 'none',
          cursor: 'pointer',
          fontSize: '100%',
          fontWeight: 'bold',
          color: style?.text_review_button_text_color || '#ffffff',
          background: style?.text_review_button_bg_color || '#2b2b33',
        }}
        dangerouslySetInnerHTML={{
          __html: style?.text_review_send_button_indication || 'Send Data',
        }}
      />

      {showBackButton && (
        <button
          type="button"
          onClick={() => onBack?.()}
          style={{
            marginTop: '1rem',
            fontSize: '18px',
            textDecoration: 'underline',
            color: style?.back_button_text_color || '#07296A',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            alignSelf: 'center',
          }}
        >
          <span
            dangerouslySetInnerHTML={{
              __html: style?.back_button_indication || 'Back',
            }}
          />
        </button>
      )}

      {showSkipNextScreenButton && (
        <button
          type="button"
          onClick={onNext}
          style={{
            marginTop: '0.5rem',
            fontSize: '18px',
            textDecoration: 'underline',
            color: style?.skip_next_screen_button_text_color || '#07296A',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            alignSelf: 'center',
          }}
        >
          <span
            dangerouslySetInnerHTML={{
              __html: style?.skip_next_screen_button_indication || 'Skip',
            }}
          />
        </button>
      )}
    </div>
  );
};

export default DeviceDataScreen;
